import React, {useEffect, useRef, useState} from 'react';
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../../components/dialogs/DialogContent';
import TextField from '../../../../../components/fields/textfield/TextField';
import {t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslation} from '../../CommonTranslation';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import Snackbar from "../../snackbar/Snackbar";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {useFindAllReferenceDefinitionsQuery} from "../reference/ReferenceDefinitionQuery";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {
    StereotypeLayoutDefinitionCreateDto,
    StereotypeLayoutDefinitionDto,
    StereotypeLayoutDefinitionUpdateDto
} from "../../../../../common/apis/stereotype/StereotypeLayoutDefinitionDto";
import {SaveButton} from '../../../../../components/button/SaveButton';
import {Button} from "../../../../../components/button/Button";
import {SimpleStereotypeLayoutGenerator} from "./SimpleStereotypeLayoutGenerator";
import {StereotypeDto} from '../../../../../common/apis/stereotype/StereotypeDto';
import {Grid} from "@mui/material";
import {AutoFixHigh} from "@mui/icons-material";
import {useAttributeDefinitionQuery} from "../attributes/AttributeDefinitionQuery";
import {StereotypeLayoutGenerator} from "./StereotypeLayoutGenerator";

interface LayoutDefinitionUpdateDialogProps {
    open: boolean;
    stereotype: StereotypeDto;
    layoutDefinition: StereotypeLayoutDefinitionDto | undefined;
    onUpdated: (layoutDefinitionId: string) => void;
    onClose: () => void;
}

export function LayoutDefinitionUpdateDialog({ open, stereotype, layoutDefinition, onUpdated, onClose }: Readonly<LayoutDefinitionUpdateDialogProps>) {
    const stereotypeApiClient = useRef(new StereotypeApiClient());
    const layoutGenerator = useRef<StereotypeLayoutGenerator>(new SimpleStereotypeLayoutGenerator())

    const [version, setVersion] = useState<Partial<string>>("");
    const [layout, setLayout] = useState<Partial<string>>("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    const {data: attributes} = useAttributeDefinitionQuery(stereotype.id);
    const {data: references} = useFindAllReferenceDefinitionsQuery(stereotype.id);

    useEffect(() => {
        if (open) {
            if (layoutDefinition) {
                setVersion(layoutDefinition.version);
                setLayout(stringifyToFormattedJson(layoutDefinition.layout));
            } else {
                setVersion("1");
                setLayout("");
            }
            setErrors({});
        }
    }, [open, layoutDefinition]);

    function saveLayoutDefinition() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const layoutObject = parseJson(layout);
        if (!layoutDefinition) {
            createLayoutDefinition(stereotype.id, version, layoutObject);
        } else {
            updateLayoutDefinition(stereotype.id, layoutDefinition.id, version, layoutObject);
        }
    }

    function parseJson(json: string): Object {
        try {
            return JSON.parse(json);
        } catch {
            return {};
        }
    }

    function stringifyToFormattedJson(object: Object): string {
        try {
            return JSON.stringify(object, null, "  ");
        } catch {
            return "";
        }
    }

    function createLayoutDefinition(stereotypeId: string, version: string, layout: Object) {
        const layoutDefinitionCreate: StereotypeLayoutDefinitionCreateDto = {
            version: version.trim(),
            layout: layout,
        };

        stereotypeApiClient.current.createLayout(stereotypeId, layoutDefinitionCreate)
                .then((definitionId) => {
                    onUpdated(definitionId);
                    onClose();
                    Snackbar.success(t(CommonTranslation.DATA_SAVED_SUCESSFULLY));
                })
                .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error))
                .finally(() => setInProgress(false));
    }

    function updateLayoutDefinition(stereotypeId: string, layoutDefinitionId: string, version: string, layout: Object) {
        const layoutDefinitionUpdate: StereotypeLayoutDefinitionUpdateDto = {
            version: version.trim(),
            layout: layout,
        };

        stereotypeApiClient.current.updateLayout(stereotypeId, layoutDefinitionId, layoutDefinitionUpdate)
                .then(() => {
                    onUpdated(layoutDefinitionId);
                    onClose();
                    Snackbar.success(t(CommonTranslation.DATA_SAVED_SUCESSFULLY));
                })
                .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error))
                .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!version.trim()) {
            newErrors.version = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        }

        if (!layout) {
            newErrors.layout = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        } else {
            try {
                JSON.parse(layout);
            } catch (_) {
                newErrors.layout = t(StereotypesTranslationKey.INVALID_LAYOUT_FORMAT);
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function generateLayout() {
        if (!attributes || !references) {
            return;
        }

        try {
            setInProgress(true);
            const layout = layoutGenerator.current.generateLayout(stereotype, attributes, references);
            setLayout(stringifyToFormattedJson(layout));
        } finally {
            setInProgress(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <DialogTitle title={t(StereotypesTranslationKey.LAYOUT)}
                    onDialogClosed={onClose} />
            <DialogContent>
                <Grid container spacing={2} marginTop={0}>
                    <Grid item xs={12} sm={5}>
                        <TextField
                                label={t(StereotypesTranslationKey.VERSION)}
                                value={version}
                                onChange={value => setVersion(value)}
                                required={true}
                                errorMessage={errors.version}
                                fullWidth/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                label={t(StereotypesTranslationKey.LAYOUT)}
                                value={layout}
                                onChange={value => setLayout(value)}
                                required={true}
                                multiline
                                rows={20}
                                InputProps={{sx: theme => ({fontSize: theme.typography.caption.fontSize})}}
                                errorMessage={errors.layout}
                                fullWidth/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <ButtonLayout>
                    <NegativeButtons>
                        <Button label={t(StereotypesTranslationKey.GENERATE_LAYOUT)}
                                icon={<AutoFixHigh color={"primary"}/>}
                                color={"primary"}
                                variant={"outlined"}
                                onClick={generateLayout}
                                disabled={inProgress && !!attributes} />
                        <CancelButton onClick={onClose} disabled={inProgress} />
                    </NegativeButtons>
                    <PositiveButtons>
                        <SaveButton onClick={saveLayoutDefinition} disabled={inProgress} />
                    </PositiveButtons>
                </ButtonLayout>
            </DialogActions>
        </Dialog>
    );
}